import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, RefObject } from "react";
import Dropdown from "../../../components/common/Dropdown";
import MultiCheckboxSelect, {
  Option,
} from "../../../components/common/MultiCheckboxSelect";
import DateRange from "../../../components/common/DatePicker/DateRange";
import ErrorBanner, {
  ValidationError,
} from "../../../components/common/ErrorBanner";
import {
  TEAM_REPORTING_ALL_AREA_OPTION,
  TEAM_REPORTING_ALL_AREA_CODE,
  TEAM_REPORTING_DEFAULT_OPTION,
  TEAM_REPORTING_SELECT_VALUE,
} from "../../../constants/teamReportingConstant";
import { loadSearchFromSession } from "../../../utils/searchSessionStorage";
import type {
  TeamReportingOption,
  TeamReportingSearchFilters,
  TeamsSearchContainerProps,
} from "../../../types/teamReporting.types";


const getTodayAsMMDDYYYY = (): string => {
  const today = new Date();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const yyyy = today.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

const parseDateToUTC = (value?: string): number | null => {
  if (!value) return null;
  // Strictly require MM-DD-YYYY: exactly 2-digit month, 2-digit day, 4-digit year
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const mm = Number(match[1]);
  const dd = Number(match[2]);
  const yyyy = Number(match[3]);
  if (mm < 1 || mm > 12 || dd < 1) return null;
  // Validate actual days-in-month (handles leap years)
  const daysInMonth = new Date(yyyy, mm, 0).getDate();
  if (dd > daysInMonth) return null;
  return Date.UTC(yyyy, mm - 1, dd);
};

  /**
   * Renders the filter form used by the active team reporting page.
   * Roles MultiCheckboxSelect: selects all by default, and always sends all selected role IDs as comma-separated string to API.
   * @returns The team reporting search container.
   */
function TeamsSearchContainer({
  regioncd,
  regionOptions,
  areaOptions,
  ratingOptions,
  rolesOptions,
  leadsDropdownOptions,
  onRegionChange,
  onAreaChange,
  onSearchAllLeadRolesChange,
  onLeadMemberNoResultChange,
  onSearch,
  restoredRegionCode,
  restoredAreaCode,
}: Readonly<TeamsSearchContainerProps>): JSX.Element {
  const hasAppliedProfileRegionRef = useRef(false);
  const hasRestoredAreaRef = useRef(false);
  const leadMemberFieldRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const dateRangeRef = useRef<HTMLDivElement>(null);

  const availableRegionOptions = useMemo(
    () =>
      regionOptions.length > 0
        ? regionOptions
        : [TEAM_REPORTING_DEFAULT_OPTION],
    [regionOptions],
  );
  const availableAreaOptions = useMemo(() => {
    const normalizedSelectValue = TEAM_REPORTING_SELECT_VALUE.toLowerCase();
    const normalizedAreaOptions = areaOptions.filter((option) => {
      const optionId = String(option.id).trim().toLowerCase();
      const optionLabel = option.label.trim().toLowerCase();
      const optionValue = option.value.trim().toLowerCase();

      return (
        optionId !== normalizedSelectValue &&
        optionLabel !== normalizedSelectValue &&
        optionValue !== normalizedSelectValue
      );
    });

    if (normalizedAreaOptions.length === 0) {
      return [TEAM_REPORTING_ALL_AREA_OPTION];
    }

    const normalizedAllAreaCode = TEAM_REPORTING_ALL_AREA_CODE.toLowerCase();
    const hasAllOption = normalizedAreaOptions.some((option) => {
      const optionId = String(option.id).trim().toLowerCase();
      const optionLabel = option.label.trim().toLowerCase();
      const optionValue = option.value.trim().toLowerCase();

      return (
        optionId === normalizedAllAreaCode ||
        optionLabel === normalizedAllAreaCode ||
        optionValue === normalizedAllAreaCode
      );
    });

    return hasAllOption
      ? normalizedAreaOptions
      : [TEAM_REPORTING_ALL_AREA_OPTION, ...normalizedAreaOptions];
  }, [areaOptions]);

  const [selectedRegion, setSelectedRegion] = useState<TeamReportingOption>(
    availableRegionOptions[0],
  );
  const [selectedArea, setSelectedArea] = useState<TeamReportingOption>(
    availableAreaOptions[0],
  );
  const [selectedRating, setSelectedRating] = useState<(string | number)[]>([]);
  // Always select all roles by default on load and when rolesOptions changes
  const [selectedRoles, setSelectedRoles] = useState<(string | number)[]>(
    rolesOptions && rolesOptions.length > 0 ? rolesOptions.map((role) => role.id) : []
  );
  useEffect(() => {
    if (rolesOptions && rolesOptions.length > 0) {
      setSelectedRoles(rolesOptions.map((role) => role.id));
    }
  }, [rolesOptions]);
  const [leadMemberName, setLeadMemberName] = useState("");
  const [selectedLeadNuid, setSelectedLeadNuid] = useState<string>("");
  const [isLeadOptionsOpen, setIsLeadOptionsOpen] = useState(false);
  const [searchAllLeadRoles, setSearchAllLeadRoles] = useState(false);
  const [rangeStart, setRangeStart] = useState<string | undefined>();
  const [rangeEnd, setRangeEnd] = useState<string | undefined>(
    getTodayAsMMDDYYYY(),
  );
  const today = useMemo(() => new Date(), []);
  const [startDateError, setStartDateError] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  // Tracks whether the user has clicked Search at least once; gates inline date error display.
  const [hasAttemptedSearch, setHasAttemptedSearch] = useState(false);

  const filteredLeadOptions = useMemo(() => {
    const normalizedQuery = leadMemberName.trim().toLowerCase();

    if (!normalizedQuery) {
      return leadsDropdownOptions;
    }

    return leadsDropdownOptions.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery),
    );
  }, [leadMemberName, leadsDropdownOptions]);

  const hasUnmatchedLeadMemberName = useMemo(() => {
    const normalizedLeadMemberName = leadMemberName.trim().toLowerCase();

    if (!searchAllLeadRoles || !normalizedLeadMemberName) {
      return false;
    }

    if (selectedLeadNuid) {
      return false;
    }

    return !leadsDropdownOptions.some(
      (option) =>
        option.label.trim().toLowerCase() === normalizedLeadMemberName,
    );
  }, [
    leadMemberName,
    leadsDropdownOptions,
    searchAllLeadRoles,
    selectedLeadNuid,
  ]);

  const isLeadMemberSelected = useMemo(
    () => searchAllLeadRoles && Boolean(selectedLeadNuid),
    [searchAllLeadRoles, selectedLeadNuid],
  );

  useEffect(() => {
    if (hasAppliedProfileRegionRef.current) {
      return;
    }

    // Session data takes priority over profile region — check first (independent
    // of regioncd/regionOptions) so it settles on mount and short-circuits all
    // later runs instead of waiting for the profile to load.
    if (loadSearchFromSession()?.selectedRegionCode) {
      hasAppliedProfileRegionRef.current = true;
      return;
    }

    if (!regioncd || !regionOptions.length) {
      return;
    }

    const matchedRegion = regionOptions.find(
      (option) => String(option.id) === regioncd || option.value === regioncd,
    );

    if (!matchedRegion) {
      return;
    }

    setSelectedRegion(matchedRegion);
    setSelectedArea(TEAM_REPORTING_ALL_AREA_OPTION);
    onAreaChange?.(TEAM_REPORTING_ALL_AREA_OPTION);
    onRegionChange(matchedRegion);
    hasAppliedProfileRegionRef.current = true;
  }, [onAreaChange, onRegionChange, regioncd, regionOptions]);

  /**
   * Restore session state from props passed by parent (only region and area)
   */
  useEffect(() => {
    if (restoredRegionCode && regionOptions.length > 0) {
      const matchedRegion = regionOptions.find(
        (option) =>
          String(option.id) === restoredRegionCode ||
          String(option.value) === restoredRegionCode,
      );
      if (matchedRegion) {
        setSelectedRegion(matchedRegion);
      }
    }
    if (restoredAreaCode && areaOptions.length > 0 && availableAreaOptions.length > 0) {
      const matchedArea = availableAreaOptions.find(
        (option) =>
          String(option.id) === restoredAreaCode ||
          String(option.value) === restoredAreaCode,
      );
      if (matchedArea) {
        hasRestoredAreaRef.current = true;
        setSelectedArea(matchedArea);
      }
    }
  }, [
    restoredRegionCode,
    restoredAreaCode,
    regionOptions,
    areaOptions,
    availableAreaOptions,
  ]);

  useEffect(() => {
    // Reset the restoration flag when areaOptions change to allow new restoration attempts
    if (areaOptions.length === 0) {
      hasRestoredAreaRef.current = false;
    }
  }, [areaOptions]);

  useEffect(() => {
    setSelectedArea((currentArea) => {
      // If area was already restored from session storage, don't override it
      if (hasRestoredAreaRef.current) {
        return currentArea;
      }

      const normalizedAllAreaCode = TEAM_REPORTING_ALL_AREA_CODE.toLowerCase();
      const allAreaOption = availableAreaOptions.find((option) => {
        const optionId = String(option.id).trim().toLowerCase();
        const optionLabel = option.label.trim().toLowerCase();
        const optionValue = option.value.trim().toLowerCase();

        return (
          optionId === normalizedAllAreaCode ||
          optionLabel === normalizedAllAreaCode ||
          optionValue === normalizedAllAreaCode
        );
      });

      const matchedArea = availableAreaOptions.find(
        (option) => option.id === currentArea.id,
      );

      const nextArea =
        matchedArea ?? allAreaOption ?? TEAM_REPORTING_ALL_AREA_OPTION;

      // Only notify parent if the area ID actually changed (prevents duplicate API calls)
      if (currentArea.id !== nextArea.id) {
        onAreaChange?.(nextArea);
      }

      return nextArea;
    });
  }, [availableAreaOptions, onAreaChange]);

  useEffect(() => {
    // Clear lead member selection when region or area changes
    // (but keep the checkbox checked so leads can reload for the new region/area)
    setLeadMemberName("");
    setSelectedLeadNuid("");
    setIsLeadOptionsOpen(false);
    onLeadMemberNoResultChange?.(false);
  }, [selectedRegion, selectedArea, onLeadMemberNoResultChange]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const fieldRoot = leadMemberFieldRef.current;
      if (!fieldRoot) {
        return;
      }

      if (!fieldRoot.contains(event.target as Node)) {
        setIsLeadOptionsOpen(false);
      }
    };

    if (isLeadOptionsOpen) {
      document.addEventListener("pointerdown", handlePointerDown, true);
    }

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isLeadOptionsOpen]);

  const handleRegion = useCallback(
    (option: TeamReportingOption) => {
      setSelectedRegion(option);
      setSelectedArea(TEAM_REPORTING_ALL_AREA_OPTION);
      onAreaChange?.(TEAM_REPORTING_ALL_AREA_OPTION);
      onRegionChange(option);
    },
    [onAreaChange, onRegionChange],
  );

  const handleArea = useCallback(
    (option: TeamReportingOption) => {
      setSelectedArea(option);
      onAreaChange?.(option);
    },
    [onAreaChange],
  );

  const handleRatingChange = useCallback((selected: Option["id"][]) => {
    setSelectedRating(selected);
  }, []);

  const handleRolesChange = useCallback(
    (selected: Option["id"][]) => {
      if (isLeadMemberSelected) {
        return;
      }
      setSelectedRoles(selected);
    },
    [isLeadMemberSelected],
  );

  const handleLeadMemberSelect = useCallback(
    (option: TeamReportingOption) => {
      onLeadMemberNoResultChange?.(false);

      const allRoleIds = rolesOptions.map((roleOption) => roleOption.id);

      if (option.value === TEAM_REPORTING_SELECT_VALUE) {
        if (selectedLeadNuid) {
          setSelectedRoles(allRoleIds);
        }
        setLeadMemberName("");
        setSelectedLeadNuid("");
        setIsLeadOptionsOpen(false);
        return;
      }

      setLeadMemberName(option.label);
      setSelectedLeadNuid(String(option.id));
      setIsLeadOptionsOpen(false);
    },
    [onLeadMemberNoResultChange, rolesOptions, selectedLeadNuid],
  );

  const handleLeadMemberInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onLeadMemberNoResultChange?.(false);
      if (selectedLeadNuid) {
        const allRoleIds = rolesOptions.map((option) => option.id);
        setSelectedRoles(allRoleIds);
      }
      setLeadMemberName(event.target.value);
      setSelectedLeadNuid("");
      setIsLeadOptionsOpen(true);
    },
    [onLeadMemberNoResultChange, rolesOptions, selectedLeadNuid],
  );

  const handleSearchAllLeadRolesToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setSearchAllLeadRoles(checked);
      if (!checked) {
        const allRoleIds = rolesOptions.map((option) => option.id);
        setLeadMemberName("");
        setSelectedLeadNuid("");
        setSelectedRoles(allRoleIds);
        setIsLeadOptionsOpen(false);
        onLeadMemberNoResultChange?.(false);
      }
      onSearchAllLeadRolesChange?.(checked);
    },
    [onLeadMemberNoResultChange, onSearchAllLeadRolesChange, rolesOptions],
  );

  useEffect(() => {
    if (!isLeadMemberSelected) {
      return;
    }

    const allRoleIds = rolesOptions.map((option) => option.id);
    setSelectedRoles(allRoleIds);
  }, [isLeadMemberSelected, rolesOptions]);

  const handleSearchClick = useCallback(() => {
    // Mark that search has been attempted so inline errors become visible
    setHasAttemptedSearch(true);

    if (startDateError) {
      setValidationErrors([
        {
          field: "startAssessmentDate",
          message: startDateError,
          fieldRef: dateRangeRef as RefObject<HTMLElement>,
        },
      ]);
      return;
    }

    if (hasUnmatchedLeadMemberName) {
      onLeadMemberNoResultChange?.(true);
    } else {
      onLeadMemberNoResultChange?.(false);
    }

    setValidationErrors([]);

    const searchFilters: TeamReportingSearchFilters = {};

    if (selectedRegion.value !== TEAM_REPORTING_SELECT_VALUE) {
      searchFilters.region = selectedRegion.id;
    }
    if (selectedArea.value !== TEAM_REPORTING_SELECT_VALUE) {
      searchFilters.area = selectedArea.id;
    }
    if (selectedRating.length > 0) {
      searchFilters.rating = selectedRating.join(",");
    }
    if (selectedRoles.length > 0) {
      // If all roles are selected, send 'All', else send comma-separated IDs
      const allRoleIds = rolesOptions.map((role) => role.id);
      const allSelected =
        selectedRoles.length === allRoleIds.length &&
        allRoleIds.every((id) => selectedRoles.includes(id));
      searchFilters.roles = allSelected ? "All" : selectedRoles.join(",");
    }
    if (rangeStart) {
      searchFilters.startDate = rangeStart;
    }
    if (rangeEnd) {
      searchFilters.endDate = rangeEnd;
    }
    if (selectedLeadNuid) {
      searchFilters.roleNuid = selectedLeadNuid;
    }



    onSearch(searchFilters);
  }, [
    onSearch,
    hasUnmatchedLeadMemberName,
    onLeadMemberNoResultChange,
    rangeEnd,
    rangeStart,
    rolesOptions.length,
    selectedArea,
    selectedLeadNuid,
    selectedRating,
    selectedRegion,
    selectedRoles,
    startDateError,
    leadMemberName,
    searchAllLeadRoles,
  ]);

  useEffect(() => {
    if (validationErrors.length === 0) {
      return;
    }

    requestAnimationFrame(() => {
      const bannerElement = bannerRef.current;
      if (!bannerElement) {
        return;
      }

      const top =
        globalThis.scrollY + bannerElement.getBoundingClientRect().top - 16;
      globalThis.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

      globalThis.setTimeout(() => {
        bannerElement.focus({ preventScroll: true });
      }, 350);
    });
  }, [validationErrors]);

  const handleDateChange = useCallback(
    (startDate?: string, endDate?: string) => {
      // Hide inline error and banner when the user modifies either date field
      setHasAttemptedSearch(false);
      setValidationErrors([]);
      setRangeStart(startDate);
      setRangeEnd(endDate);
    },
    [],
  );

  useEffect(() => {
    const sTs = parseDateToUTC(rangeStart);
    const eTs = parseDateToUTC(rangeEnd);
    const todayTs = Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    // Check if BOTH dates are invalid format first
    if (rangeStart && sTs === null && rangeEnd && eTs === null) {
      setStartDateError(
        "Start and end assessment dates are not valid (MM-DD-YYYY).",
      );
    }
    // Check individual invalid formats
    else if (rangeStart && sTs === null) {
      setStartDateError(
        "Start assessment date is not a valid date (MM-DD-YYYY).",
      );
    } else if (rangeEnd && eTs === null) {
      setStartDateError(
        "End assessment date is not a valid date (MM-DD-YYYY).",
      );
    }
    // Check if BOTH dates are future dates
    else if (sTs !== null && sTs > todayTs && eTs !== null && eTs > todayTs) {
      setStartDateError(
        "Start and end assessment dates cannot be future dates.",
      );
    }
    // Check individual future dates
    else if (sTs !== null && sTs > todayTs) {
      setStartDateError("Start assessment date cannot be a future date.");
    } else if (eTs !== null && eTs > todayTs) {
      setStartDateError("End assessment date cannot be a future date.");
    }
    // Check date range order
    else if (sTs !== null && eTs !== null && sTs > eTs) {
      setStartDateError(
        `Start date ${rangeStart ?? "MM-DD-YYYY"} is greater than end date ${rangeEnd ?? "MM-DD-YYYY"}.`,
      );
    } else {
      setStartDateError("");
    }
  }, [rangeStart, rangeEnd, today]);

  // After a search attempt, auto-clear the banner as soon as the date error is resolved.
  useEffect(() => {
    if (hasAttemptedSearch && !startDateError) {
      setValidationErrors([]);
    }
  }, [hasAttemptedSearch, startDateError]);

  return (
    <div className="bg-white border-solid border-2 border-border-light w-236 rounded-xl p-8! mt-4!">
      <h3 className="h3 mb-2 mt-0!">Filter Teams</h3>
      <p className="p3 -book md:mb-0!">Select criteria below to refine the search</p>

      {validationErrors.length > 0 && (
        <div
          ref={bannerRef}
          tabIndex={-1}
          aria-live="polite"
          className="mt-8! mb-4!"
        >
          <ErrorBanner errors={validationErrors} />
        </div>
      )}

      <div className="flex w-full! gap-x-8 mt-8! mb-10!">
        <div className="flex-1">
          <Dropdown
            label="Region"
            selectedValue={selectedRegion}
            options={availableRegionOptions}
            onSelect={handleRegion}
            menuClassName="w-full! max-h-60! overflow-y-auto!"
            buttonClassName="w-full!"
          />
        </div>
        <div className="flex-1">
          <Dropdown
            label="Area"
            selectedValue={selectedArea}
            options={availableAreaOptions}
            onSelect={handleArea}
            menuClassName="w-full! max-h-60! overflow-y-auto!"
            buttonClassName="w-full!"
          />
        </div>
      </div>

      <div className="flex w-full! gap-x-8 mb-8!">
        <div className="flex-1">
          <MultiCheckboxSelect
            label="Rating"
            options={ratingOptions}
            selectedValue={selectedRating}
            onChange={handleRatingChange}
            isSelectAll={false}
            hideSelectAll={true}
          />
        </div>
        <div className="flex-1">
          <MultiCheckboxSelect
            label="Roles"
            options={rolesOptions}
            selectedValue={selectedRoles}
            onChange={handleRolesChange}
            isSelectAll={true}
            disabled={isLeadMemberSelected}
          />
        </div>
      </div>

      <div className="flex w-[48%]! gap-x-8 mb-8!">
        <div className="flex-1!" ref={dateRangeRef}>
          <DateRange
            startValue={rangeStart}
            endValue={rangeEnd}
            startLabelName="Start Assessment Date"
            endLabelName="End Assessment Date"
            required
            helperText={startDateError ? undefined : "MM-DD-YYYY"}
            onChange={handleDateChange}
            maxDate={today}
          />
          {hasAttemptedSearch && startDateError ? (
            <div className="error-description" data-testid="start-date-error">
              <ul id="text-input-error-description-list-start-date">
                <li>
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    width="14"
                    height="14"
                  />
                  <span className="screenreader-only">Error</span>
                  {startDateError}
                </li>
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center">
        <button type="button" className="flex items-center">
          <input
            className="check-box"
            name="Search all lead roles"
            type="checkbox"
            id="checkbox-search-all-lead-roles"
            checked={searchAllLeadRoles}
            onChange={handleSearchAllLeadRolesToggle}
          />
          <label
            className="cursor-pointer pl-4.5! m-0!"
            htmlFor="checkbox-search-all-lead-roles"
          >
            <span aria-hidden="true" className="pr-2!"></span> Search all lead
            roles
          </label>
        </button>
      </div>

      {searchAllLeadRoles && (
        <div
          ref={leadMemberFieldRef}
          className="ds-form w-full! mt-8!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <label htmlFor="lead-member-name-input">Lead member name</label>
          <div className="relative w-[40%]!">
            <input
              id="lead-member-name-input"
              type="text"
              autoComplete="off"
              className="mt-1! w-full!"
              value={leadMemberName}
              onChange={handleLeadMemberInputChange}
              onFocus={() => setIsLeadOptionsOpen(true)}
              placeholder={TEAM_REPORTING_SELECT_VALUE}
            />
            {isLeadOptionsOpen && (
              <div
                id="lead-member-name-options"
                className="ds-dropdown__menu-list -expanded w-full! max-h-60! overflow-y-auto! absolute z-10 bg-white border rounded-md shadow-md mt-1!"
                style={{ border: "1px solid #006FAB" }}
              >
                {(filteredLeadOptions.length > 0
                  ? filteredLeadOptions
                  : [TEAM_REPORTING_DEFAULT_OPTION]
                ).map((option, index) => (
                  <div
                    key={`${option.id}-${index}`}
                    className={`p-0! drop-menu-list-op ${String(option.id) === selectedLeadNuid ? "active" : ""}`}
                  >
                    <button
                      type="button"
                      className="group w-full text-left flex items-center py-3! pl-3! pr-4! font-bold hover:bg-[#00395C] hover:text-white"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleLeadMemberSelect(option);
                      }}
                    >
                      <span className="drop-menu-list-text ml-2! font-bold group-hover:text-white">
                        {option.label}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-x-4 mt-8!">
        <button
          className="button py-0! -primary -light"
          data-analytics-type="button"
          data-analytics-click="Default"
          onClick={handleSearchClick}
        >
          Search
        </button>
      </div>
    </div>
  );
}

export default memo(TeamsSearchContainer);
